import { Icon } from "@iconify/react";
import { AxiosError } from "axios";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import FileInput from "../../components/shared/FileInput";
import {
  CheckBoxWithLabel,
  Input,
  Label,
} from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import DocumentTypes from "../../constants/DocumentTypes";
import { useGetAllPlanDocs, useUpdatePlanDocs } from "../../queries/esopPlan";
import {
  DocumentInterface,
  DocumentType,
} from "../../types/DocumentsInterface";
import {
  convertBase64ToBlob,
  downloadBlobObject,
  downloadS3File,
} from "../../utils/DownloadFile";
import convertToBase64 from "../../utils/convertToBase64";
import { queryClient } from "../../queries/client";
import InfoBox from "../../components/shared/InfoBox";
import CheckboxComp from "../../shared/checkbox";
import { useGetEmployeePortalSettings } from "../../queries/siteSettings";

export default function PlanDocument({
  planId,
  isViewMode,
}: {
  planId: string;
  isViewMode: boolean;
}) {
  const { mutate: updatePlanDoc, isLoading } = useUpdatePlanDocs();
  const { data: employeePortalSettings } = useGetEmployeePortalSettings();
  const canEmployeeViewDocs = useMemo(
    () => employeePortalSettings?.showPlanToEmployees ?? false,
    [employeePortalSettings]
  );
  const { data: existingPlanDocs } = useGetAllPlanDocs(planId ?? "");
  const validationSchema = Yup.array().of(
    Yup.object({
      documentName: Yup.string().required("Document Name is required"),
      documentType: Yup.string()
        .required("Document Type is required")
        .test(
          "unique-documentType",
          "Document type already exists",
          (value) => {
            if (value && value === DocumentType.OTHERS) {
              return true;
            }
            existingPlanDocs?.forEach((doc) => {
              if (doc.documentType === value && doc.id) {
                return false;
              } else {
                return true;
              }
            });
            return true;
          }
        ),
    })
  );
  const allowedExtensions = ["pdf"];

  function handleSubmit(values: DocumentInterface[]) {
    const errors = values.some(
      (e) => !e.documentName || !e.documentType || (e.toBeAdded && !e.b64File)
    );
    const filesNotUploaded = values.some((e) => e.toBeAdded && !e.b64File);
    if (filesNotUploaded) {
      toast("Please upload all the selected files before submitting.", {
        type: "error",
        autoClose: 3000,
      });
      return;
    }
    if (errors) {
      toast("Please fix the validation errors before submitting.", {
        type: "error",
        autoClose: 3000,
      });
      return;
    }
    if (existingPlanDocs !== values) {
      updatePlanDoc(values, {
        async onSuccess() {
          toast(
            `Document${values.length > 1 ? "s" : ""} modified successfully!`,
            {
              type: "success",
              autoClose: 3000,
            }
          );
        },
        onError(error) {
          toast((error as AxiosError).response?.data as string, {
            type: "error",
            autoClose: 3000,
          });
        },
      });
    }
  }
  async function handleFileInput(
    e: any,
    index: number,
    availablePlanDocs: DocumentInterface[]
  ) {
    const file = e.target.files[0] as File;
    if (file) {
      const splitFileName = file.name.split(".");
      const fileExtension = splitFileName[splitFileName.length - 1];
      if (!allowedExtensions.includes(fileExtension))
        toast(
          `Invalid file type, only ${allowedExtensions.toString()} are allowed`,
          { type: "error", autoClose: 3000 }
        );
      else {
        const b64File = (await convertToBase64(file)) as string;
        formik.setValues(
          availablePlanDocs.map((doc, i) =>
            i === index
              ? {
                  ...doc,
                  b64File,
                  contentType: file.type,
                  isEdited: true,
                  isUserUploaded: true,
                }
              : doc
          )
        );
      }
    }
  }
  function handleAdd(availablePlanDocs: DocumentInterface[]) {
    const newDocument = baseDocument;
    newDocument.isUserUploaded = true;
    formik.setValues([...availablePlanDocs, newDocument]);
  }
  function fetchAndViewDocument(path: string, fileName?: string) {
    downloadS3File(path, fileName || "", false);
  }

  async function downloadReport(document: DocumentInterface) {
    if (document.b64File) {
      const blob = await convertBase64ToBlob(
        document.b64File,
        document.contentType || "application/pdf"
      );
      downloadBlobObject(
        blob,
        `${document.documentName}.pdf` || "Document.pdf"
      );
    }
  }
  function getdocTypefromValue(key: string) {
    switch (key) {
      case DocumentType.ESOP_PLAN_DOCUMENT:
        return "Plan Document";
      case DocumentType.ESOP_BOARD_DOCUMENT:
        return "Board Documents";
      case DocumentType.OTHERS:
        return "Other Documents";
      default:
        return "";
    }
  }
  function getUpdatedDocumentTypes(documents: DocumentInterface[]) {
    const documentTypesSet = new Set<string>(
      documents.map((d) => d.documentType)
    );
    const documentTypesArray = Array.from(documentTypesSet);
    const documentTypes: {
      text: string;
      value: DocumentType;
    }[] = [];
    if (
      !documentTypesArray.includes(DocumentType.ESOP_PLAN_DOCUMENT) &&
      documents.filter(
        (d) => d.documentType === DocumentType.ESOP_PLAN_DOCUMENT
      ).length < 1
    ) {
      documentTypes.push({
        text: "Plan Document",
        value: DocumentType.ESOP_PLAN_DOCUMENT,
      });
    }
    if (
      !documentTypesArray.includes(DocumentType.ESOP_BOARD_DOCUMENT) &&
      documents.filter(
        (d) => d.documentType === DocumentType.ESOP_BOARD_DOCUMENT
      ).length < 1
    ) {
      documentTypes.push({
        text: "Board Documents",
        value: DocumentType.ESOP_BOARD_DOCUMENT,
      });
    }
    documentTypes.push({ text: "Other Documents", value: DocumentType.OTHERS });
    return documentTypes;
  }
  function handleDelete(
    deleteIndex: number,
    availablePlanDocs: DocumentInterface[]
  ) {
    formik.setValues(
      availablePlanDocs.reduce((newDocs: DocumentInterface[], doc, index) => {
        if (index === deleteIndex) {
          if (!doc.toBeAdded) {
            newDocs.push({ ...doc, toBeDeleted: true });
          }
        } else {
          newDocs.push(doc);
        }
        return newDocs;
      }, [])
    );
  }
  const formik = useFormik({
    initialValues: existingPlanDocs || [],
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      handleSubmit(values);
    },
  });
  const canEmployeeViewPlanDocs = useMemo(
    () => formik.values?.some((val) => val.metaData?.shownToEmployee) ?? false,
    [formik.values]
  );
  const isSaveAvailable = useMemo(() => {
    if (isViewMode) return false;
    if (existingPlanDocs?.length !== formik.values.length) return true;
    for (let i = 0; i < formik.values.length; i++) {
      const existingDoc = existingPlanDocs?.[i];
      const currentDoc = formik.values[i];
      if (
        existingDoc?.documentName !== currentDoc.documentName ||
        existingDoc?.documentType !== currentDoc.documentType ||
        existingDoc?.metaData?.shownToEmployee !==
          currentDoc.metaData?.shownToEmployee ||
        existingDoc?.b64File !== currentDoc.b64File ||
        existingDoc?.toBeDeleted !== currentDoc.toBeDeleted
      ) {
        return true;
      }
    }
    return false;
  }, [formik.values, existingPlanDocs, isViewMode]);
  useEffect(() => {
    if (existingPlanDocs) {
      formik.setValues(formik.values);
      formik.resetForm({ values: existingPlanDocs });
    }
  }, [existingPlanDocs]);
  const availableDocTypes = useMemo(
    () => getUpdatedDocumentTypes(formik.values),
    [formik.values]
  );

  const baseDocument: DocumentInterface = {
    documentName: "",
    documentType: DocumentType.OTHERS,
    toBeAdded: true,
    entityId: planId ?? "",
    isUserUploaded: false,
    metaData: { shownToEmployee: false, canEmployeeDownload: false },
  };
  return (
    <div className="bg-white shadow-md rounded-md p-4">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="px-4 py-2 text-left text-xs font-normal">
              Document Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-normal">
              Document Type
            </th>
            <th className="px-4 py-2"></th>
            <th className="px-4 py-2"></th>
            <th className="px-4 py-2 text-left text-xs font-normal">
              Visible to Employee
            </th>
            <th className="px-4 py-2 text-center text-xs font-normal">
              Uploaded By
            </th>
          </tr>
        </thead>
        <tbody>
          {formik.values.map((document, index) => (
            <tr key={index} className="">
              <td className="px-4 py-2">
                <Input
                  type="text"
                  disabled={isViewMode || !!document.id}
                  {...formik.getFieldProps(`${[index]}.documentName`)}
                />
                {formik.touched[index]?.documentName &&
                  formik.errors[index]?.documentName && (
                    <Error text={formik.errors[index]?.documentName} />
                  )}
              </td>
              <td className="px-4 py-2">
                {document.id ? (
                  <Input
                    disabled
                    value={getdocTypefromValue(
                      formik.values[index].documentType
                    )}
                  />
                ) : (
                  <>
                    {(() => {
                      // Ensure the current value is always present in the options
                      const currentType = formik.values[index].documentType;
                      let options = availableDocTypes;
                      if (
                        currentType &&
                        !availableDocTypes.some(
                          (opt) => opt.value === currentType
                        )
                      ) {
                        options = [
                          ...availableDocTypes,
                          {
                            text: getdocTypefromValue(currentType),
                            value: currentType as DocumentType,
                          },
                        ];
                      }
                      return (
                        <Select
                          isPlaceHolderDisabled={true}
                          options={options}
                          valueGetter={(o) => o.value}
                          textGetter={(o) => o.text}
                          disabled={isViewMode || !!document.id}
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${[index]}.documentType`,
                              e.target.value
                            );
                          }}
                          value={currentType}
                        />
                      );
                    })()}
                    {formik.touched[index]?.documentType &&
                      formik.errors[index]?.documentType && (
                        <Error text={formik.errors[index]?.documentType} />
                      )}
                  </>
                )}
              </td>
              <td className="px-4 py-2">
                <HStack className="items-center gap-x-2">
                  <Icon
                    width={20}
                    className="text-gray-400 cursor-pointer hover:text-orange-501"
                    onClick={() => {
                      if (document.documentPath) {
                        fetchAndViewDocument(
                          document.documentPath ?? "",
                          document.documentName
                        );
                      } else {
                        downloadReport(document);
                      }
                    }}
                    icon={"ic:baseline-remove-red-eye"}
                  />
                  {!isViewMode &&
                    (document.toBeDeleted ? (
                      <div className="text-xs text-red-600">
                        {" "}
                        Will be deleted
                      </div>
                    ) : (
                      <Icon
                        width={20}
                        className="text-gray-400 cursor-pointer hover:text-orange-501"
                        onClick={() => handleDelete(index, formik.values)}
                        icon={"ic:baseline-delete"}
                      />
                    ))}
                </HStack>
              </td>
              <td className="px-4 py-2">
                {!isViewMode &&
                  (document.b64File !== undefined ? (
                    document.toBeAdded ? (
                      <div className="text-xs text-green-400">
                        Will be uploaded
                      </div>
                    ) : (
                      <div className="text-xs text-green-400">
                        Will be edited
                      </div>
                    )
                  ) : (
                    <FileInput
                      file={`${
                        document.documentPath ? "Edit Document" : "Upload"
                      }`}
                      className="cursor-not-allowed"
                      id={index.toString()}
                      accept="application/pdf"
                      disabled={!baseDocument}
                      onChange={(e) => {
                        handleFileInput(e, index, formik.values);
                      }}
                    />
                  ))}
              </td>
              <td className="px-4 py-2 justify-between align-middle">
                <input
                  type="checkbox"
                  checked={document.metaData?.shownToEmployee || false}
                  onChange={() => {
                    const updatedMetaData = {
                      ...document.metaData,
                      shownToEmployee: !document.metaData?.shownToEmployee,
                    };
                    formik.setValues(
                      formik.values.map((doc, i) =>
                        i === index
                          ? {
                              ...doc,
                              metaData: updatedMetaData,
                            }
                          : doc
                      )
                    );
                  }}
                  className="text-white accent-orange-501"
                  name=""
                />
              </td>
              <td className="px-4 py-2 text-center">
                {document.isUserUploaded ? (
                  <div className="text-xs text-gray-201">User uploaded</div>
                ) : (
                  <div className="text-xs text-gray-201">System Generated</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isViewMode && availableDocTypes.length > 0 && (
        <div className="flex justify-center">
          <ButtonSecondary
            className="items-center py-1 mt-4 font-medium"
            onClick={() => handleAdd(formik.values)}
          >
            Add a Document
          </ButtonSecondary>
        </div>
      )}
      {!canEmployeeViewDocs && canEmployeeViewPlanDocs && (
        <HStack className="pt-8">
          <InfoBox
            message={
              'Employees will not be able to view the plan documents unless the "Show Plan to Employees" setting is enabled in Employee Portal Settings.'
            }
          />
        </HStack>
      )}
      <div className="flex justify-end pt-4">
        {!isViewMode && (
          <div className="px-4">
            <ButtonPrimary
              disabled={!isSaveAvailable || isLoading}
              onClick={() => formik.handleSubmit()}
              loading={isLoading}
            >
              Save
            </ButtonPrimary>
          </div>
        )}
      </div>
    </div>
  );
}
